import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Share, Plus, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

const InstallPWA = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already installed
    const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone === true;
    setIsStandalone(isStandaloneMode);
    
    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);
    
    // Check if dismissed before
    const wasDismissed = localStorage.getItem('pwa-install-dismissed');
    const dismissedTime = wasDismissed ? parseInt(wasDismissed) : 0;
    const hoursSinceDismiss = (Date.now() - dismissedTime) / (1000 * 60 * 60);
    
    if (hoursSinceDismiss > 24) {
      localStorage.removeItem('pwa-install-dismissed');
    } else if (wasDismissed) {
      setDismissed(true);
    }

    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (!isStandaloneMode && !dismissed) {
        setTimeout(() => setShowPrompt(true), 3000); // Show after 3 seconds
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    // Show iOS prompt after delay
    if (isIOSDevice && !isStandaloneMode && !dismissed) {
      setTimeout(() => setShowPrompt(true), 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, [dismissed]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  if (isStandalone || !showPrompt) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="fixed bottom-20 left-4 right-4 z-50 max-w-md mx-auto"
      >
        <div className="rounded-3xl overflow-hidden"
             style={{
               background: 'rgba(10, 15, 20, 0.92)',
               backdropFilter: 'blur(24px)',
               border: '1px solid rgba(34, 211, 238, 0.25)',
               boxShadow: '0 18px 50px rgba(0,0,0,0.45), 0 0 30px rgba(34,211,238,0.15)',
             }}>
          {/* Header */}
          <div className="p-4 flex items-center justify-between"
               style={{
                 background: 'linear-gradient(135deg, rgba(15,118,110,0.3), rgba(34,211,238,0.1))',
                 borderBottom: '1px solid rgba(34,211,238,0.2)',
               }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                   style={{ background: 'rgba(34,211,238,0.15)', border: '1px solid rgba(34,211,238,0.3)' }}>
                <Smartphone className="w-5 h-5 text-cyan-300" />
              </div>
              <div>
                <h3 className="text-white font-bold text-sm" style={{ fontFamily: 'Syne, sans-serif' }}>Instalar App</h3>
                <p className="text-cyan-200/70 text-xs">Acesso rápido na tela inicial</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
              data-testid="pwa-install-close"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
          
          {/* Content */}
          <div className="p-4">
            {isIOS ? (
              // iOS Instructions
              <div className="space-y-3">
                <p className="text-cyan-100/80 text-sm">
                  Para instalar o app no seu iPhone ou iPad:
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 p-3 rounded-xl"
                       style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                         style={{ background: 'rgba(34,211,238,0.2)' }}>
                      <Share className="w-4 h-4 text-cyan-300" />
                    </div>
                    <span className="text-sm text-white/90">
                      1. Toque em <strong className="text-cyan-300">Compartilhar</strong>
                    </span>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-xl"
                       style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                         style={{ background: 'rgba(212,162,86,0.2)' }}>
                      <Plus className="w-4 h-4 text-amber-300" />
                    </div>
                    <span className="text-sm text-white/90">
                      2. Selecione <strong className="text-amber-300">Adicionar à Tela de Início</strong>
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              // Android/Desktop
              <div className="space-y-3">
                <p className="text-cyan-100/80 text-sm">
                  Instale o AJ HealthMap para acesso rápido, mesmo offline.
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={handleInstall}
                    className="flex-1 h-11 rounded-xl text-white font-bold"
                    style={{ background: 'linear-gradient(135deg, #0F766E, #22D3EE)', boxShadow: '0 6px 20px rgba(34,211,238,0.35)' }}
                    data-testid="pwa-install-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Instalar
                  </Button>
                  <Button
                    onClick={handleDismiss}
                    variant="outline"
                    className="h-11 rounded-xl"
                    style={{ border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)', background: 'transparent' }}
                  >
                    Depois
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InstallPWA;
